// Loads everything the screens read for the active session; refetches when PlanProvider bumps `version`.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { ItemType, ScanRun, WatchedItem } from '../../domain/types';
import { api, type FindingView, type FixView, type SessionView } from '../lib/api';
import { usePlan } from './PlanProvider';

export interface AppData {
  session: SessionView;
  findings: FindingView[];
  watchlist: WatchedItem[];
  quotas: Record<ItemType, { limit: number; used: number; preview: boolean }>;
  fixes: FixView[];
  scans: ScanRun[];
  osint: { mode: string; nextAvailableAt: string | null };
}

interface DataCtx { data: AppData | null; loading: boolean; error: string | null; refresh: () => Promise<void> }
const Ctx = createContext<DataCtx | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const { ready, version } = usePlan();
  const [data, setData] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [session, f, w, x, s] = await Promise.all([api.session(), api.findings(), api.watchlist(), api.fixes(), api.scans()]);
      setData({ session, findings: f.findings, watchlist: w.items, quotas: w.quotas, fixes: x.fixes, scans: s.scans, osint: s.osint });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (ready) void refresh(); }, [ready, version, refresh]);

  return <Ctx.Provider value={{ data, loading, error, refresh }}>{children}</Ctx.Provider>;
}

export function useData() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useData outside DataProvider');
  return c;
}
